import React, { useMemo, useState } from "react";
import { Box, Grid, Paper, Typography, useMediaQuery } from "@mui/material";
import DriveEtaOutlinedIcon from "@mui/icons-material/DriveEtaOutlined";
import { useTheme } from "@mui/material/styles";

import {
  BasicDetailsStep,
  StickyModalFooter,
  WizardModalLayout,
} from "./wizard/WizardComponents";

const getBasicErrors = (form) => {
  const errors = {};
  if (!form.username.trim()) errors.username = "Driver name is required";
  if (!form.uniqueId.trim()) errors.uniqueId = "Unique ID is required";
  if (!form.mobileNo.trim()) errors.mobileNo = "Mobile is required";
  if (!form.password.trim()) errors.password = "Password is required";
  return errors;
};

const summaryItemSx = {
  borderRadius: 3,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
  p: 1.6,
};

const AddDriverModal = ({ onClose, onSubmit }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [currentStep, setCurrentStep] = useState(0);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    mobileNo: "",
    uniqueId: "",
    address: "",
    licenseNo: "",
    licenseValidity: "",
    salary: "",
    password: "",
  });

  const steps = [
    { key: "basic", title: "Basic Details", description: "Driver account information" },
    { key: "preferences", title: "Driver Details", description: "License and address" },
    { key: "permissions", title: "Review", description: "Confirm before submit" },
  ];

  const basicErrors = useMemo(() => getBasicErrors(formData), [formData]);
  const visibleBasicErrors = showStepErrors && currentStep === 0 ? basicErrors : {};

  const updateField = (path, value) => {
    setFormData((prev) => ({
      ...prev,
      [path]: path === "mobileNo" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleNext = () => {
    if (currentStep === 0 && Object.keys(basicErrors).length) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = () => {
    if (Object.keys(basicErrors).length) {
      setCurrentStep(0);
      setShowStepErrors(true);
      return;
    }
    onSubmit(formData);
  };

  const basicFields = [
    { path: "username", label: "Driver Name", required: true },
    { path: "uniqueId", label: "Unique ID", required: true },
    { path: "mobileNo", label: "Mobile", required: true, inputProps: { inputMode: "numeric" } },
    {
      path: "password",
      label: "Password",
      type: "password",
      required: true,
      md: 12,
    },
  ];

  const extraFields = [
    { path: "address", label: "Address", md: 12 },
    { path: "licenseNo", label: "License No" },
    { path: "licenseValidity", label: "Validity", type: "date", inputLabelProps: { shrink: true } },
    { path: "salary", label: "Salary", type: "number" },
  ];

  return (
    <WizardModalLayout
      open
      onClose={onClose}
      icon={DriveEtaOutlinedIcon}
      title="Add Driver"
      subtitle="Create a driver account in simple guided steps."
      steps={steps}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      helpTitle="Need Help?"
      helpText="Fill the basic details first, then complete the optional driver information before submitting."
      fullScreen={fullScreen}
      maxWidth="lg"
      footer={
        <StickyModalFooter
          currentStep={currentStep}
          steps={steps}
          loading={false}
          onBack={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
          onCancel={onClose}
          onNext={handleNext}
          onSubmit={handleSubmit}
          submitLabel="Create Driver"
          submitLoadingLabel="Saving..."
        />
      }
    >
      {currentStep === 0 ? (
        <BasicDetailsStep
          title="Driver Information"
          description="Enter the required driver account details."
          form={formData}
          onChange={updateField}
          fields={basicFields}
          errors={visibleBasicErrors}
          tip="These same fields are still passed to the existing submit handler."
        />
      ) : null}

      {currentStep === 1 ? (
        <BasicDetailsStep
          title="Additional Driver Details"
          description="Add license, address, and salary information if available."
          form={formData}
          onChange={updateField}
          fields={extraFields}
          errors={{}}
        />
      ) : null}

      {currentStep === 2 ? (
        <Paper
          sx={{
            borderRadius: 4,
            border: "1px solid #dbe4f0",
            backgroundColor: "#fff",
            p: { xs: 2, sm: 2.5 },
            boxShadow: "0 20px 40px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Grid container spacing={2}>
            {[
              ["Driver Name", formData.username || "-"],
              ["Unique ID", formData.uniqueId || "-"],
              ["Mobile", formData.mobileNo || "-"],
              ["Address", formData.address || "-"],
              ["License No", formData.licenseNo || "-"],
              ["Validity", formData.licenseValidity || "-"],
              ["Salary", formData.salary || "-"],
            ].map(([label, value]) => (
              <Grid item xs={12} md={6} key={label}>
                <Box sx={summaryItemSx}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>{label}</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : null}
    </WizardModalLayout>
  );
};

export default AddDriverModal;
